import { Currency, MAX_INPUT_VALUE } from "@inrev/common";
import { Option } from "@inrev/core";
import { type FocusEvent, useLayoutEffect, useRef, useState } from "react";
import {
	type FieldError,
	type FieldPath,
	type FieldValues,
	type UseControllerProps,
	useController,
} from "react-hook-form";
import { FormFieldError } from "../FormFieldError";
import { Input } from "../inputs/Input";

const CURRENCY_NOISE = /[\s\u00a0,$€£¥]/g;
const ACCOUNTING_NEGATIVE = /^\((.*)\)$/;
/** A focus this soon after a press on the field came from that press. */
const POINTER_FOCUS_WINDOW_MS = 500;
const NUMERIC_DRAFT = /^-?\d*\.?\d*$/;

const stripCurrencyNoise = (raw: string, currency: string): string => {
	const withoutSymbol = currency ? raw.split(currency).join("") : raw;
	const stripped = withoutSymbol.replace(CURRENCY_NOISE, "");
	const accountingNegative = ACCOUNTING_NEGATIVE.exec(stripped);
	return accountingNegative ? `-${accountingNegative[1]}` : stripped;
};

export type FormMoneyProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, FieldPath<TFieldValues>, TTransformedValues> & {
	compact?: boolean;
	className?: string;
	containerClassName?: string;
	placeholder?: string;
	disabled?: boolean;
	errorMessage?: string;
	hideErrorMessage?: boolean;
	min?: number;
	max?: number;
	currency?: string;
	rightAlign?: boolean;
	testId?: string;
	id?: string;
	onValueBlur?: () => void;
	onValueChange?: () => void;
};

export const FormMoney = <TFieldValues extends FieldValues, TTransformedValues = TFieldValues>({
	name,
	control,
	defaultValue,
	compact,
	className,
	containerClassName,
	placeholder = "0.00",
	disabled,
	errorMessage,
	hideErrorMessage = false,
	min,
	max = MAX_INPUT_VALUE,
	currency = "$",
	rightAlign = false,
	testId,
	id,
	onValueBlur,
	onValueChange,
}: FormMoneyProps<TFieldValues, TTransformedValues>) => {
	const {
		field: { onChange, onBlur, value, ...field },
		fieldState: { error },
	} = useController<TFieldValues, FieldPath<TFieldValues>, TTransformedValues>({
		name,
		control,
		defaultValue,
	});

	const [inputValue, setInputValue] = useState<string>("");
	const [isFocused, setIsFocused] = useState(false);
	const [rangeError, setRangeError] = useState<string | null>(null);
	const clearedRef = useRef(false);
	const pointerDownAtRef = useRef(Number.NEGATIVE_INFINITY);
	const selectAfterRenderRef = useRef<HTMLInputElement | null>(null);
	const rangeFieldError: FieldError | undefined = rangeError
		? { type: "range", message: rangeError }
		: undefined;
	const displayError = rangeFieldError ?? error;
	const hasError = !!displayError;

	const formatWithCommas = (str: string): string => {
		if (!str) return str;
		const parts = str.split(".");
		const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
		return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
	};

	const formatNumber = (num: number | null | undefined): string => {
		if (Option.isNone(num)) return "";
		if (isNaN(num)) return "";
		const str = num.toFixed(2);
		const parts = str.split(".");
		const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
		return `${integerPart}.${parts[1]}`;
	};

	const displayValue = isFocused
		? formatWithCommas(inputValue)
		: formatNumber(Option.isNone(value) || value === "" ? null : Number(value));

	/**
	 * Focus swaps the formatted text for the raw number, which drops the select-all a browser gives
	 * a tabbed-into field, so typing would append to the old amount. The selection is restored once
	 * the swap renders, before paint and the next keystroke. A click keeps its caret. Chrome's
	 * `select()` also focuses the input, so it is skipped once focus has moved on.
	 */
	useLayoutEffect(() => {
		const input = selectAfterRenderRef.current;
		selectAfterRenderRef.current = null;
		if (input && input.ownerDocument.activeElement === input) input.select();
	});

	const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
		const fromPointer = event.timeStamp - pointerDownAtRef.current < POINTER_FOCUS_WINDOW_MS;
		clearedRef.current = false;
		selectAfterRenderRef.current = fromPointer ? null : event.currentTarget;
		setIsFocused(true);
		setRangeError(null);
		setInputValue(Option.mapOrElse(value, String, ""));
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const cleaned = stripCurrencyNoise(e.target.value, currency);

		if (cleaned === "") {
			clearedRef.current = true;
			setInputValue("");
			setRangeError(null);
			onChange(null);
			return;
		}

		clearedRef.current = false;

		if (!NUMERIC_DRAFT.test(cleaned)) return;

		if (cleaned.endsWith(".") || cleaned === "-") {
			setInputValue(cleaned);
			return;
		}

		const numValue = Number(cleaned);
		if (!isNaN(numValue)) {
			if (max !== undefined && numValue > max) {
				if (rangeError) return;
				setRangeError("Too large");
			} else {
				setRangeError(null);
			}
			setInputValue(cleaned);
			onChange(numValue);
			onValueChange?.();
		}
	};

	const handleBlur = () => {
		const wasCleared = clearedRef.current;
		clearedRef.current = false;
		setIsFocused(false);
		setInputValue("");

		const numValue = Option.isNone(value) || value === "" ? null : Number(value);
		if (!wasCleared && Option.isSome(numValue) && !isNaN(numValue)) {
			const rounded = Currency.round(numValue);
			if (rounded !== numValue) {
				onChange(rounded);
			}
			if (max !== undefined && rounded > max) {
				setRangeError("Too large");
			} else if (min !== undefined && rounded < min) {
				setRangeError("Too small");
			} else {
				setRangeError(null);
			}
		}
		onBlur();
		onValueBlur?.();
	};

	return (
		<div className="relative">
			<Input
				{...field}
				id={id}
				value={displayValue}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onPointerDown={(event) => {
					pointerDownAtRef.current = event.timeStamp;
				}}
				compact={compact}
				className={className}
				containerClassName={containerClassName}
				placeholder={placeholder}
				disabled={disabled}
				type="text"
				inputMode="decimal"
				error={hasError}
				rightAlign={rightAlign}
				prefix={currency}
				testId={testId}
			/>
			{hasError && !hideErrorMessage && (
				<FormFieldError error={displayError} errorMessage={errorMessage} />
			)}
		</div>
	);
};
